import { LOCALE_TO_ROUTE } from "./jump-to-string"
import type { StringLocation } from "../../scripts/string-locations-build-core"

/**
 * Whether a queue item can be shown in context on a real page, and if not, why.
 * - `page`     — renders on a real page (from the crawl index, or a namespace→page
 *                mapping + a route for its locale); navigable.
 * - `chrome`   — shared UI shown across the site, not tied to one page (crawl marked
 *                it chromeLike, or no jumpPath / no route). Reviewed out of context.
 * - `template` — the EN value carries ICU args/plurals, so it can't be text-matched
 *                in the DOM even if it has a page. Reviewed out of context too.
 *
 * `template` wins over `chrome`/`page` because the highlight would fail regardless.
 * The crawl index (`location`) is preferred when present; otherwise we fall back to
 * the namespace heuristic so flag-gated keys the crawl can't reach still work.
 */
export type LocateState = "page" | "chrome" | "template"

export function locateState(
  item: { jumpPath: string; locale: string },
  templateFlag: boolean,
  location: StringLocation | null = null
): LocateState {
  if (templateFlag) return "template"
  if (location) {
    if (location.chromeLike) return "chrome"
    if (location.paths.length > 0) return "page"
  }
  if (!item.jumpPath) return "chrome"
  if (!LOCALE_TO_ROUTE[item.locale]) return "chrome"
  return "page"
}

/**
 * Long strings (legal copy especially) don't fit a cramped inline textarea — the
 * panel routes these to the side-by-side modal instead. "Large" = over ~280 chars
 * or containing a blank-line paragraph break.
 */
export function isLargeString(value: string): boolean {
  return value.length > 280 || /\n\s*\n/.test(value)
}
